/* eslint-disable no-console */
import Spacer from "@components/atoms/Spacer";
import Fonts from "@constants/Fonts";
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { StyleSheet, ScrollView, Platform } from "react-native";
import { object, string } from "@lib/util/yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { View, Text, Card, Wrapper } from "@components/atoms/Themed";
import Colors from "@constants/Colors";
import CopyButton from "@components/atoms/CopyButton";
import KeyboardAvoidingView from "@components/molecules/KeyboardAvoidingView";
import AttachmentsUploader, {
  isUploadedFile,
} from "@components/molecules/AttachmentsUploader";
import { resolveError } from "@lib/util/error";
import { ImageFile } from "@components/molecules/ImagePicker";
import { MediaTypeOptions } from "expo-image-picker";
import InputAccessoryView from "@components/atoms/InputAccessoryView";
import { makeUploadablesFromFileList } from "@lib/util/relaySupport";
import ErrorDialog from "@components/molecules/dialog/ErrorDialog";
import {
  TextArea,
  CopyField,
  ErrorText,
} from "@components/molecules/TextInput";
import GradientButton from "@components/atoms/GradientButton";
import { PostDraftInstagram$key } from "@generated/PostDraftInstagram.graphql";
import { PostDraftInstagramMutation } from "@generated/PostDraftInstagramMutation.graphql";
import {
  KeyboardId,
  InstagramContentMaxLength,
  PostMaxImageSize,
  PostMaxLimitSize,
  PostMaxVideoSize,
} from "@constants/App";
import {
  graphql,
  useFragment,
  useMutation,
  ConnectionHandler,
} from "react-relay/hooks";
import Divider from "@components/atoms/Divider";
import ConfirmDialog from "@components/organisms/PostDraft/PostDraftConfirmDialog";
import { PostDraftInstagramRemoveMutation } from "@generated/PostDraftInstagramRemoveMutation.graphql";
import { log as sentryLog, notify } from "@lib/util/logger";
import { appVersion } from "@constants/Env";
import { DeviceUtils } from "@lib/util/device";

const chatDraft = graphql`
  fragment PostDraftInstagram on Post {
    id
    content
    script
    influencerId
    attachments {
      id
      file
      duration
      contentType
    }
    campaign {
      id
      mediaType
      purpose
      avoidedSentence
      socialNetwork {
        hashTag
        productUrl
        mentionTagContent
      }
    }
  }
`;

const postMutation = graphql`
  mutation PostDraftInstagramMutation(
    $input: UpdatePostMutationInput!
    $connections: [ID!]!
  ) {
    updatePost(input: $input) {
      __typename
      ... on UpdatePostSuccess {
        post {
          id
          status
          content
          attachments {
            file
            duration
            contentType
          }
        }
        commentEdges @prependEdge(connections: $connections) {
          cursor
          node {
            id
            content
            action
            stamp
            extraInfo
            extraType
            createdAt
            commentable {
              userType
              avatar {
                file
              }
            }
            ogps {
              id
              title
              image
              url
              description
            }
            attachments {
              id
              file
              duration
              contentType
            }
            post {
              id
            }
          }
        }
      }
      ... on UpdatePostError {
        message
      }
    }
  }
`;

const removeImagesMutation = graphql`
  mutation PostDraftInstagramRemoveMutation(
    $input: RemoveAttachmentsMutationInput!
  ) {
    removeAttachments(input: $input) {
      __typename
      ... on Attachments {
        attachments {
          id
        }
      }
      ... on RemoveAttachmentsError {
        message
      }
    }
  }
`;

type InputData = {
  content: string | null;
  script: string | null;
};

export default function PostDraftInstagram({
  postFragment,
  status,
  onClose,
  onChange,
  onLoading,
}: {
  postFragment: PostDraftInstagram$key;
  status: "draft" | "modify" | "review" | "approved";
  onClose: () => void;
  onChange: (isChange: boolean) => void;
  onLoading: (isLoading: boolean) => void;
}) {
  const data = useFragment(chatDraft, postFragment);
  const [selectedImages, setSelectedImages] = useState<ImageFile[]>([]);
  const [postable, setPostable] = useState<boolean>(false);
  const [confirm, setConfirm] = useState<boolean>(false);
  const [error, setError] = useState<{
    message: string;
    details?: Record<string, unknown>;
  } | null>(null);
  const [removeImageList, setRemoveImageList] = useState<string[] | null>(null);
  const [commitRemoveImages] =
    useMutation<PostDraftInstagramRemoveMutation>(removeImagesMutation);
  const [commit] = useMutation<PostDraftInstagramMutation>(postMutation);

  const connectionID = ConnectionHandler.getConnectionID(
    data.id,
    "Chat_comments"
  );

  const uploaded = useMemo(() => data.attachments.map((row) => row), [data]);

  const canEdit = useMemo<boolean>(
    () => ["draft", "modify"].indexOf(status) !== -1,
    [status]
  );
  const hashTag = useMemo<string | null>(() => {
    const tags =
      data.campaign.socialNetwork.hashTag !== null
        ? data.campaign.socialNetwork.hashTag
            .split(",")
            .map((value) => `#${value}`)
        : [];
    if (tags.length > 0) {
      return tags.join(" ");
    }
    return null;
  }, [data]);

  const showGuide = useMemo<boolean>(
    () =>
      !!(
        status !== "approved" &&
        (hashTag !== null ||
          data.campaign.socialNetwork.mentionTagContent !== null ||
          data.campaign.socialNetwork.productUrl !== null)
      ),
    [data, hashTag, status]
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<InputData>({
    defaultValues: {
      content: data.content,
      script: data.script,
    },
    mode: "all",
    resolver: yupResolver(
      object().shape({
        content: string()
          .default(null)
          .trim()
          .max(
            InstagramContentMaxLength,
            `投稿内容は${InstagramContentMaxLength}文字以内で入力してください`
          )
          .test("notEmpty", "入力してください", (value) => {
            if (
              data.campaign.mediaType !== "ins_live" &&
              (value === "" || value === null)
            ) {
              return false;
            }
            return true;
          })
          .nullable(),
        script: string()
          .default(null)
          .trim()
          .max(
            InstagramContentMaxLength,
            `台本は${InstagramContentMaxLength}文字以内で入力してください`
          )
          .test("notEmpty", "入力してください", (value) => {
            if (
              data.campaign.mediaType === "ins_live" &&
              (value === "" || value === null)
            ) {
              return false;
            }
            return true;
          })
          .nullable(),
      })
    ),
  });

  // コンポーネント初期表示時とアンマウント時のログ
  useEffect(() => {
    try {
      const initialLog = {
        type: "PostDraft:Mounted",
        postId: data.id,
        influencerId: data.influencerId ?? "unknown",
        campaignId: data.campaign?.id ?? "unknown",
        status,
        initialState: {
          campaignMediaType: data.campaign.mediaType,
          existingAttachmentsCount: data.attachments.length,
          hasContent: data.content !== null && data.content !== "",
          hasScript: data.script !== null && data.script !== "",
          contentLength: data.content?.length ?? 0,
          scriptLength: data.script?.length ?? 0,
        },
        timestamp: new Date().toISOString(),
        environment: {
          appVersion,
          platform: Platform.OS,
          osVersion: DeviceUtils.osVersion,
          deviceModel: DeviceUtils.modelName,
          isDevice: DeviceUtils.isDevice,
        },
      };
      notify(initialLog, { level: "info" }).catch(() => {
        // ログ送信失敗を無視
      });
    } catch {
      // ログ処理の例外を完全に無視
    }

    // クリーンアップ: コンポーネントアンマウント時のログ
    return () => {
      try {
        const unmountLog = {
          type: "PostDraft:Unmounted",
          postId: data.id,
          influencerId: data.influencerId ?? "unknown",
          campaignId: data.campaign?.id ?? "unknown",
          status,
          finalState: {
            postable,
            isValid,
            selectedImagesCount: selectedImages.length,
            attachmentsCount: data.attachments.length,
          },
          timestamp: new Date().toISOString(),
          environment: {
            appVersion,
            platform: Platform.OS,
            osVersion: DeviceUtils.osVersion,
            deviceModel: DeviceUtils.modelName,
            isDevice: DeviceUtils.isDevice,
          },
        };
        notify(unmountLog, { level: "info" }).catch(() => {
          // ログ送信失敗を無視
        });
      } catch {
        // ログ処理の例外を完全に無視
      }
    };
  }, [data, status, postable, isValid, selectedImages.length]);

  useEffect(() => {
    let requiredImage = true;
    if (data.campaign.mediaType !== "ins_live") {
      const removeImageNum =
        removeImageList !== null ? removeImageList.length : 0;

      if (
        data.attachments.length - removeImageNum === 0 &&
        selectedImages.length === 0
      ) {
        requiredImage = false;
      }
    }
    const newPostable = isValid && canEdit && requiredImage;
    setPostable(newPostable);

    // ボタンが無効化された場合のログ
    if (!newPostable) {
      try {
        const removeImageNum =
          removeImageList !== null ? removeImageList.length : 0;
        const buttonDisabledLog = {
          type: "PostDraft:ButtonDisabled",
          postId: data.id,
          influencerId: data.influencerId ?? "unknown",
          campaignId: data.campaign?.id ?? "unknown",
          status,
          reason: {
            requiredImage,
            isValid,
            canEdit,
            selectedImagesCount: selectedImages.length,
            attachmentsCount: data.attachments.length,
            removeImageCount: removeImageNum,
            effectiveAttachmentsCount: data.attachments.length - removeImageNum,
            campaignMediaType: data.campaign.mediaType,
          },
          timestamp: new Date().toISOString(),
          environment: {
            appVersion,
            platform: Platform.OS,
            osVersion: DeviceUtils.osVersion,
            deviceModel: DeviceUtils.modelName,
            isDevice: DeviceUtils.isDevice,
          },
        };
        notify(buttonDisabledLog, { level: "warning" }).catch(() => {
          // ログ送信失敗を無視
        });
      } catch {
        // ログ処理の例外を完全に無視
      }
    }
  }, [isValid, canEdit, selectedImages, removeImageList, data, status]);

  const save = useCallback(async () => {
    await handleSubmit(async ({ script, content }: InputData) => {
      const startTime = Date.now();
      const diagnosticContext = {
        postId: data.id,
        influencerId: data.influencerId ?? "unknown",
        campaignId: data.campaign?.id ?? "unknown",
        campaignMediaType: data.campaign.mediaType,
        postStatus: status,
        environment: {
          appVersion,
          platform: Platform.OS,
          osVersion: DeviceUtils.osVersion,
          deviceModel: DeviceUtils.modelName,
          isDevice: DeviceUtils.isDevice,
          networkState:
            typeof navigator !== "undefined" ? navigator.onLine : undefined,
        },
      };

      try {
        // 保存処理開始
        try {
          const phase1Log = {
            ...diagnosticContext,
            phase: "1_SAVE_STARTED",
            timestamp: new Date().toISOString(),
            elapsedMs: 0,
            filesCount: selectedImages.filter((img) => !isUploadedFile(img))
              .length,
            existingAttachmentsCount: data.attachments.length,
            removeImagesCount: removeImageList?.length ?? 0,
          };
          notify(
            {
              type: "PostDraft:Diagnostic:Phase1",
              ...phase1Log,
            },
            { level: "info" }
          ).catch(() => {
            // ログ送信失敗を無視（既存処理に影響させない）
          });
        } catch {
          // ログ処理の例外を完全に無視（既存処理を保護）
        }

        setConfirm(false);
        onLoading(true);
        const files: File[] = [];
        selectedImages.forEach((image) => {
          if (!isUploadedFile(image)) {
            files.push(image.file);
          }
        });

        // ファイル処理開始前
        const phase2Time = Date.now();
        const filesInfo = files.map((file) => ({
          name: file.name,
          size: file.size,
          sizeMB: (file.size / (1024 * 1024)).toFixed(2),
          type: file.type,
        }));
        const totalSizeMB =
          files.reduce((sum, f) => sum + f.size, 0) / (1024 * 1024);
        try {
          const phase2Log = {
            ...diagnosticContext,
            phase: "2_BEFORE_FILE_PROCESSING",
            timestamp: new Date().toISOString(),
            elapsedMs: phase2Time - startTime,
            filesCount: files.length,
            filesInfo,
            totalSizeMB: totalSizeMB.toFixed(2),
            networkType:
              Platform.OS === "ios" && totalSizeMB > 50
                ? "LARGE_FILE_ON_MOBILE"
                : "NORMAL",
          };
          notify(
            {
              type: "PostDraft:Diagnostic:Phase2",
              ...phase2Log,
            },
            { level: "info" }
          ).catch(() => {
            // ログ送信失敗を無視（既存処理に影響させない）
          });
        } catch {
          // ログ処理の例外を完全に無視（既存処理を保護）
        }

        const uploadables = await makeUploadablesFromFileList(
          "variables.input.files",
          files,
          data.influencerId ?? undefined
        );

        // ファイル処理完了後
        const phase3Time = Date.now();
        try {
          const phase3Log = {
            ...diagnosticContext,
            phase: "3_AFTER_FILE_PROCESSING",
            timestamp: new Date().toISOString(),
            elapsedMs: phase3Time - startTime,
            processingDurationMs: phase3Time - phase2Time,
            uploadablesKeys:
              uploadables != null ? Object.keys(uploadables) : [],
            uploadablesCount:
              uploadables != null ? Object.keys(uploadables).length : 0,
          };
          notify(
            {
              type: "PostDraft:Diagnostic:Phase3",
              ...phase3Log,
            },
            { level: "info" }
          ).catch(() => {
            // ログ送信失敗を無視（既存処理に影響させない）
          });
        } catch {
          // ログ処理の例外を完全に無視（既存処理を保護）
        }

        await new Promise<void>((resolve, reject) => {
          if (removeImageList !== null && removeImageList.length > 0) {
            commitRemoveImages({
              variables: {
                input: {
                  ids: removeImageList,
                },
              },
              onCompleted({ removeAttachments }) {
                if (removeAttachments.__typename === "Attachments") {
                  resolve();
                } else {
                  const errorMessage =
                    removeAttachments.__typename === "RemoveAttachmentsError"
                      ? removeAttachments.message
                      : "削除できませんでした";

                  const errorInfo = {
                    action: "remove_attachments_failed",
                    postId: data.id,
                    influencerId: data.influencerId ?? "unknown",
                    campaignId: data.campaign?.id ?? "unknown",
                    campaignMediaType: data.campaign?.mediaType ?? "unknown",
                    postStatus: status,
                    removeImageIds: removeImageList,
                    removeImageCount: removeImageList.length,
                    errorMessage,
                    typename: removeAttachments.__typename,
                    timestamp: new Date().toISOString(),
                    environment: {
                      appVersion,
                      platform: Platform.OS,
                      osVersion: DeviceUtils.osVersion,
                      deviceModel: DeviceUtils.modelName,
                      isDevice: DeviceUtils.isDevice,
                    },
                  };
                  console.error("Failed to remove attachments:", errorInfo);

                  // Slack通知
                  try {
                    notify(
                      {
                        type: "PostDraftError: RemoveAttachments",
                        ...errorInfo,
                      },
                      { level: "error" }
                    ).catch(() => {
                      // ログ送信失敗を無視（既存処理に影響させない）
                    });
                  } catch {
                    // ログ処理の例外を完全に無視（既存処理を保護）
                  }

                  reject(errorMessage);
                }
              },
            });
          } else {
            resolve();
          }
        });

        // Relay commit 開始前
        const phase4Time = Date.now();
        try {
          const phase4Log = {
            ...diagnosticContext,
            phase: "4_BEFORE_RELAY_COMMIT",
            timestamp: new Date().toISOString(),
            elapsedMs: phase4Time - startTime,
            uploadablesCount:
              uploadables != null ? Object.keys(uploadables).length : 0,
            hasContent: content !== null && content !== "",
            hasScript: script !== null && script !== "",
          };
          notify(
            {
              type: "PostDraft:Diagnostic:Phase4",
              ...phase4Log,
            },
            { level: "info" }
          ).catch(() => {
            // ログ送信失敗を無視（既存処理に影響させない）
          });
        } catch {
          // ログ処理の例外を完全に無視（既存処理を保護）
        }

        await new Promise<void>((resolve, reject) => {
          commit({
            variables: {
              input: {
                id: data.id,
                content,
                script,
                files: selectedImages.map(() => null),
              },
              connections: [connectionID],
            },
            uploadables,
            onCompleted({ updatePost }) {
              // Relay commit 完了
              const phase5Time = Date.now();
              try {
                const phase5Log = {
                  ...diagnosticContext,
                  phase: "5_RELAY_COMMIT_COMPLETED",
                  timestamp: new Date().toISOString(),
                  elapsedMs: phase5Time - startTime,
                  commitDurationMs: phase5Time - phase4Time,
                  typename: updatePost.__typename,
                  success: updatePost.__typename === "UpdatePostSuccess",
                };
                notify(
                  {
                    type: "PostDraft:Diagnostic:Phase5",
                    ...phase5Log,
                  },
                  { level: "info" }
                ).catch(() => {
                  // ログ送信失敗を無視（既存処理に影響させない）
                });
              } catch {
                // ログ処理の例外を完全に無視（既存処理を保護）
              }

              if (updatePost.__typename === "UpdatePostSuccess") {
                resolve();
              } else {
                const errorMessage =
                  updatePost.__typename === "UpdatePostError"
                    ? updatePost.message
                    : "保存できませんでした";

                const errorInfo = {
                  action: "update_post_failed",
                  postId: data.id,
                  influencerId: data.influencerId ?? "unknown",
                  campaignId: data.campaign?.id ?? "unknown",
                  campaignMediaType: data.campaign?.mediaType ?? "unknown",
                  postStatus: status,
                  errorMessage,
                  typename: updatePost.__typename,
                  mutation: "updatePost",
                  variables: {
                    postId: data.id,
                    hasContent: content !== null && content !== "",
                    hasScript: script !== null && script !== "",
                    contentLength: content?.length ?? 0,
                    scriptLength: script?.length ?? 0,
                  },
                  filesInfo: {
                    selectedImagesCount: selectedImages.length,
                    existingAttachmentsCount: data.attachments.length,
                    removedImagesCount: removeImageList?.length ?? 0,
                  },
                  timestamp: new Date().toISOString(),
                  environment: {
                    appVersion,
                    platform: Platform.OS,
                    osVersion: DeviceUtils.osVersion,
                    deviceModel: DeviceUtils.modelName,
                    isDevice: DeviceUtils.isDevice,
                  },
                };
                console.error("Failed to update post:", errorInfo);

                // Slack通知
                try {
                  notify(
                    {
                      type: "PostDraftError: UpdatePost",
                      ...errorInfo,
                    },
                    { level: "error" }
                  ).catch(() => {
                    // ログ送信失敗を無視（既存処理に影響させない）
                  });
                } catch {
                  // ログ処理の例外を完全に無視（既存処理を保護）
                }

                reject(errorMessage);
              }
            },
            onError: (e) => {
              // Relay commit エラー
              const phaseErrorTime = Date.now();
              try {
                const phaseErrorLog = {
                  ...diagnosticContext,
                  phase: "RELAY_COMMIT_ERROR",
                  timestamp: new Date().toISOString(),
                  elapsedMs: phaseErrorTime - startTime,
                  commitDurationMs: phaseErrorTime - phase4Time,
                };
                notify(
                  {
                    type: "PostDraft:Diagnostic:RelayError",
                    ...phaseErrorLog,
                  },
                  { level: "error" }
                ).catch(() => {
                  // ログ送信失敗を無視（既存処理に影響させない）
                });
              } catch {
                // ログ処理の例外を完全に無視（既存処理を保護）
              }

              const errorInfo = {
                action: "graphql_mutation_error",
                postId: data.id,
                influencerId: data.influencerId ?? "unknown",
                campaignId: data.campaign?.id ?? "unknown",
                campaignMediaType: data.campaign?.mediaType ?? "unknown",
                postStatus: status,
                errorMessage:
                  e instanceof Error && e.message !== ""
                    ? e.message
                    : "Unknown error",
                errorStack: e instanceof Error ? e.stack : undefined,
                errorType: e instanceof Error ? e.name : typeof e,
                errorDetails:
                  e instanceof Error
                    ? {
                        name: e.name,
                        message: e.message,
                        cause: (e as unknown as { cause?: unknown }).cause,
                        graphQLErrors: (
                          e as unknown as { graphQLErrors?: unknown }
                        ).graphQLErrors,
                        networkError: (
                          e as unknown as { networkError?: unknown }
                        ).networkError,
                        // iOS向けの追加情報
                        isIOS: Platform.OS === "ios",
                        filesCount: files.length,
                        uploadablesKeys:
                          uploadables != null ? Object.keys(uploadables) : [],
                      }
                    : e,
                mutation: "updatePost",
                variables: {
                  postId: data.id,
                  content,
                  script,
                  filesCount: selectedImages.length,
                },
                timestamp: new Date().toISOString(),
                environment: {
                  appVersion,
                  platform: Platform.OS,
                  osVersion: DeviceUtils.osVersion,
                  deviceModel: DeviceUtils.modelName,
                  isDevice: DeviceUtils.isDevice,
                  networkState:
                    typeof navigator !== "undefined"
                      ? navigator.onLine
                      : undefined,
                },
              };
              console.error("GraphQL mutation error:", errorInfo);

              // Slack通知
              try {
                notify(
                  {
                    type: "PostDraftError: GraphQLMutation",
                    ...errorInfo,
                  },
                  { level: "error" }
                ).catch(() => {
                  // ログ送信失敗を無視（既存処理に影響させない）
                });
              } catch {
                // ログ処理の例外を完全に無視（既存処理を保護）
              }

              reject(e);
            },
          });
        });

        // 保存成功時のログ
        try {
          const saveSuccessLog = {
            type: "PostDraft:SaveSuccess",
            postId: data.id,
            influencerId: data.influencerId ?? "unknown",
            campaignId: data.campaign?.id ?? "unknown",
            status,
            timestamp: new Date().toISOString(),
            environment: {
              appVersion,
              platform: Platform.OS,
              osVersion: DeviceUtils.osVersion,
              deviceModel: DeviceUtils.modelName,
              isDevice: DeviceUtils.isDevice,
            },
          };
          notify(saveSuccessLog, { level: "info" }).catch(() => {
            // ログ送信失敗を無視
          });
        } catch {
          // ログ処理の例外を完全に無視
        }

        onClose();
      } catch (e: unknown) {
        onLoading(false);
        const resolvedError = resolveError(e);

        const errorDetails = {
          action: "draft_submission_failed",
          postId: data.id,
          influencerId: data.influencerId ?? "unknown",
          campaignId: data.campaign?.id ?? "unknown",
          campaignInfo: {
            mediaType: data.campaign.mediaType,
            purpose: data.campaign.purpose?.substring(0, 100),
            avoidedSentence: data.campaign.avoidedSentence?.substring(0, 100),
            hashTag: data.campaign.socialNetwork?.hashTag,
            productUrl: data.campaign.socialNetwork?.productUrl,
            mentionTagContent: data.campaign.socialNetwork?.mentionTagContent,
          },
          postStatus: status,
          submissionData: {
            hasContent: content !== null && content !== "",
            hasScript: script !== null && script !== "",
            contentLength: content?.length ?? 0,
            scriptLength: script?.length ?? 0,
          },
          filesInfo: {
            existingAttachmentsCount: data.attachments.length,
            selectedImagesCount: selectedImages.length,
            removeImageListCount: removeImageList?.length ?? 0,
            totalExpectedFiles:
              data.attachments.length +
              selectedImages.length -
              (removeImageList?.length ?? 0),
          },
          error: {
            message: resolvedError.message,
            code: resolvedError.code,
            originalError:
              e instanceof Error
                ? {
                    name: e.name,
                    message: e.message,
                    stack: e.stack,
                    cause: (e as unknown as { cause?: unknown }).cause,
                  }
                : e,
          },
          timestamp: new Date().toISOString(),
          environment: {
            appVersion,
            platform: Platform.OS,
            osVersion: DeviceUtils.osVersion,
            deviceModel: DeviceUtils.modelName,
            isDevice: DeviceUtils.isDevice,
            networkState:
              typeof navigator !== "undefined" ? navigator.onLine : undefined,
          },
        };

        setError({
          message: resolvedError.message,
          details: errorDetails,
        });

        console.error("Draft submission failed:", errorDetails);

        sentryLog("error", e, {
          extra: errorDetails,
        });

        // Slack通知
        try {
          notify(
            {
              type: "PostDraftError: SubmissionFailed",
              ...errorDetails,
            },
            { level: "error" }
          ).catch(() => {
            // ログ送信失敗を無視（既存処理に影響させない）
          });
        } catch {
          // ログ処理の例外を完全に無視（既存処理を保護）
        }
      }
    })();
  }, [
    handleSubmit,
    onLoading,
    selectedImages,
    onClose,
    removeImageList,
    commitRemoveImages,
    data.id,
    data.influencerId,
    data.campaign?.id,
    data.campaign.mediaType,
    data.campaign.purpose,
    data.campaign.avoidedSentence,
    data.campaign.socialNetwork?.hashTag,
    data.campaign.socialNetwork?.productUrl,
    data.campaign.socialNetwork?.mentionTagContent,
    data.attachments.length,
    status,
    commit,
    connectionID,
  ]);

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView keyboardVerticalOffset={48}>
        <ScrollView>
          <Wrapper>
            <Spacer height={8} />
            {data.campaign.mediaType === "ins_live" && (
              <>
                <Card style={styles.card}>
                  <Text style={styles.text}>
                    配信する大まかな台本を入力してください
                  </Text>
                  <Spacer height={16} />
                  {canEdit ? (
                    <>
                      <TextArea
                        control={control}
                        inputAccessoryViewID={KeyboardId}
                        maxLength={InstagramContentMaxLength}
                        name="script"
                        numberOfLines={9}
                        onChange={() => onChange(true)}
                        useCounter
                      />
                      {!isValid && errors.script !== undefined && (
                        <ErrorText error={errors.script.message} />
                      )}
                    </>
                  ) : (
                    <CopyField multiline value={data.script} />
                  )}
                </Card>
                <Spacer height={16} />
              </>
            )}
            <Card style={styles.card}>
              <Text style={styles.text}>
                {data.campaign.mediaType === "ins_live"
                  ? "画像や動画があれば"
                  : "Instagramに投稿する"}
                画像・動画をアップロードしてください。（最大 {PostMaxLimitSize}
                枚、写真
                {PostMaxImageSize}MB、動画{PostMaxVideoSize}
                MBまで）
              </Text>
              <Spacer height={16} />

              <AttachmentsUploader
                items={uploaded}
                maxImageSize={PostMaxImageSize}
                maxVideoSize={PostMaxVideoSize}
                onChange={(imageFiles: ImageFile[]) => {
                  setSelectedImages([...imageFiles]);
                  onChange(true);

                  // 画像選択時のログ
                  try {
                    const newFilesCount = imageFiles.filter(
                      (img) => !isUploadedFile(img)
                    ).length;
                    const imageSelectionLog = {
                      type: "PostDraft:ImageSelection",
                      postId: data.id,
                      influencerId: data.influencerId ?? "unknown",
                      campaignId: data.campaign?.id ?? "unknown",
                      status,
                      imageInfo: {
                        totalSelectedCount: imageFiles.length,
                        newFilesCount,
                        existingFilesCount: imageFiles.length - newFilesCount,
                        existingAttachmentsCount: data.attachments.length,
                      },
                      timestamp: new Date().toISOString(),
                      environment: {
                        appVersion,
                        platform: Platform.OS,
                        osVersion: DeviceUtils.osVersion,
                        deviceModel: DeviceUtils.modelName,
                        isDevice: DeviceUtils.isDevice,
                      },
                    };
                    notify(imageSelectionLog, { level: "info" }).catch(() => {
                      // ログ送信失敗を無視
                    });
                  } catch {
                    // ログ処理の例外を完全に無視
                  }
                }}
                onMessage={(errorMessage: string | null) => {
                  if (errorMessage !== null) {
                    const errorInfo = {
                      action: "file_attachment_failed",
                      postId: data.id,
                      influencerId: data.influencerId ?? "unknown",
                      campaignId: data.campaign?.id ?? "unknown",
                      campaignMediaType: data.campaign.mediaType,
                      postStatus: status,
                      errorMessage,
                      filesInfo: {
                        currentAttachmentsCount: data.attachments.length,
                        maxImageSize: PostMaxImageSize,
                        maxVideoSize: PostMaxVideoSize,
                        maxLimitSize: PostMaxLimitSize,
                      },
                      timestamp: new Date().toISOString(),
                      environment: {
                        appVersion,
                        platform: Platform.OS,
                        osVersion: DeviceUtils.osVersion,
                        deviceModel: DeviceUtils.modelName,
                        isDevice: DeviceUtils.isDevice,
                      },
                    };
                    console.error("Failed to attach file:", errorInfo);

                    // Sentryにもエラーを記録
                    sentryLog("warning", errorMessage, {
                      extra: errorInfo,
                    });

                    // Slack通知
                    try {
                      notify(
                        {
                          type: "PostDraftError: FileAttachment",
                          ...errorInfo,
                        },
                        { level: "error" }
                      ).catch(() => {
                        // ログ送信失敗を無視（既存処理に影響させない）
                      });
                    } catch {
                      // ログ処理の例外を完全に無視（既存処理を保護）
                    }
                  }
                  setError(
                    errorMessage !== null ? { message: errorMessage } : null
                  );
                }}
                onPickEnd={() => onLoading(false)}
                onPickStart={() => onLoading(true)}
                pickupMediaType={MediaTypeOptions.All}
                removeImageIds={(imageIds) => {
                  setRemoveImageList([...imageIds]);
                  onChange(true);
                }}
                selectionLimit={PostMaxLimitSize}
                uploadable={canEdit}
              />
            </Card>

            <Spacer height={16} />

            <Card style={styles.card}>
              <Text style={styles.title}>撮影の仕方</Text>
              <Spacer height={8} />
              <Text style={styles.text}>{data.campaign.purpose}</Text>
              <Spacer height={24} />
              <Text style={styles.title}>投稿に関するNG事項</Text>
              <Spacer height={8} />
              <Text style={styles.text}>{data.campaign.avoidedSentence}</Text>
            </Card>

            {data.campaign.mediaType !== "ins_live" && (
              <>
                <Spacer height={16} />

                <Card style={styles.card}>
                  <Text style={styles.text}>
                    投稿時の内容を記載してください。
                    {showGuide && (
                      <Text style={styles.alert}>
                        指定されているタグやURLがある場合は、必ず投稿内容に加えてください。
                      </Text>
                    )}
                  </Text>

                  {showGuide && (
                    <>
                      {hashTag !== null && (
                        <>
                          <Spacer height={24} />
                          <View style={styles.row}>
                            <Text style={styles.title}>指定ハッシュタグ</Text>
                            <Spacer width={8} />
                            <CopyButton value={hashTag} />
                          </View>

                          <Spacer height={8} />
                          <Text style={styles.text}>{hashTag}</Text>
                        </>
                      )}

                      {data.campaign.socialNetwork.mentionTagContent !==
                        null && (
                        <>
                          <Spacer height={24} />
                          <View style={styles.row}>
                            <Text style={styles.title}>指定メンションタグ</Text>
                            <Spacer width={8} />
                            <CopyButton
                              value={
                                data.campaign.socialNetwork.mentionTagContent
                              }
                            />
                          </View>
                          <Spacer height={8} />
                          <Text style={styles.text}>
                            {data.campaign.socialNetwork.mentionTagContent}
                          </Text>
                        </>
                      )}

                      {data.campaign.socialNetwork.productUrl !== null && (
                        <>
                          <Spacer height={24} />
                          <View style={styles.row}>
                            <Text style={styles.title}>掲載して欲しいURL</Text>
                            <Spacer width={8} />
                            <CopyButton
                              value={data.campaign.socialNetwork.productUrl}
                            />
                          </View>
                          <Spacer height={8} />
                          <Text style={styles.text}>
                            {data.campaign.socialNetwork.productUrl}
                          </Text>
                        </>
                      )}

                      <Spacer height={8} />
                    </>
                  )}

                  <Spacer height={16} />

                  {canEdit ? (
                    <>
                      <TextArea
                        control={control}
                        inputAccessoryViewID={KeyboardId}
                        maxLength={InstagramContentMaxLength}
                        name="content"
                        numberOfLines={9}
                        onChange={() => onChange(true)}
                        useCounter
                      />
                      {!isValid && errors.script !== undefined && (
                        <ErrorText error={errors.script.message} />
                      )}
                    </>
                  ) : (
                    <CopyField multiline value={data.content} />
                  )}
                </Card>
              </>
            )}
          </Wrapper>
          <Spacer height={40} />
        </ScrollView>
      </KeyboardAvoidingView>

      {status !== "approved" && status !== "review" && (
        <>
          <Divider />
          <Card style={styles.action}>
            <GradientButton
              disabled={!postable}
              onPress={() => {
                // ボタン押下時のログ
                try {
                  const buttonPressLog = {
                    type: "PostDraft:ButtonPressed",
                    postId: data.id,
                    influencerId: data.influencerId ?? "unknown",
                    campaignId: data.campaign?.id ?? "unknown",
                    status,
                    state: {
                      postable,
                      isValid,
                      canEdit,
                      selectedImagesCount: selectedImages.length,
                      attachmentsCount: data.attachments.length,
                    },
                    timestamp: new Date().toISOString(),
                    environment: {
                      appVersion,
                      platform: Platform.OS,
                      osVersion: DeviceUtils.osVersion,
                      deviceModel: DeviceUtils.modelName,
                      isDevice: DeviceUtils.isDevice,
                    },
                  };
                  notify(buttonPressLog, { level: "info" }).catch(() => {
                    // ログ送信失敗を無視
                  });
                } catch {
                  // ログ処理の例外を完全に無視
                }
                setConfirm(true);
              }}
              textStyle={styles.actionText}
              title={
                status === "draft"
                  ? "下書きを作成する"
                  : status === "modify"
                  ? "下書きを修正する"
                  : "レビュー中"
              }
            />
            {!postable && (
              <>
                <Spacer height={8} />
                <Text style={styles.error}>入力内容に不備があります</Text>
              </>
            )}
          </Card>
          <InputAccessoryView nativeID={KeyboardId} />
        </>
      )}
      {confirm && (
        <ConfirmDialog
          cancel={() => {
            // 確認ダイアログキャンセル時のログ
            try {
              const cancelLog = {
                type: "PostDraft:ConfirmDialogCanceled",
                postId: data.id,
                influencerId: data.influencerId ?? "unknown",
                campaignId: data.campaign?.id ?? "unknown",
                status,
                timestamp: new Date().toISOString(),
                environment: {
                  appVersion,
                  platform: Platform.OS,
                  osVersion: DeviceUtils.osVersion,
                  deviceModel: DeviceUtils.modelName,
                  isDevice: DeviceUtils.isDevice,
                },
              };
              notify(cancelLog, { level: "info" }).catch(() => {
                // ログ送信失敗を無視
              });
            } catch {
              // ログ処理の例外を完全に無視
            }
            setConfirm(false);
          }}
          save={save}
        />
      )}
      {error !== null && (
        <ErrorDialog
          details={error.details}
          message={error.message}
          onClose={() => setError(null)}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  card: {
    paddingVertical: 24,
    paddingHorizontal: 16,
  },
  text: {
    ...Fonts.lr130,
    color: Colors.black,
  },
  title: {
    ...Fonts.lb100,
    color: Colors.gray,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingRight: 14,
  },
  alert: {
    ...Fonts.lb130,
    color: Colors.orange,
  },
  guide: {
    ...Fonts.lr130,
    color: Colors.gray,
  },
  action: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    justifyContent: "center",
    alignItems: "center",
  },
  actionText: {
    color: Colors.white,
    ...Fonts.xlb100,
  },
  error: {
    color: Colors.orange,
  },
});
